import { omit } from "@client/lib/object";
import { type WithClassName } from "@client/types";
import { useUpdated } from "@common/lib/hooks";
import classNames from "classnames";
import * as React from "react";

export const makeWith =
  <OwnProps extends {}, TELement extends HTMLElement = HTMLElement>(
    action: (element: TELement, props: OwnProps) => void,
    optionsPropName: string,
    getClassName?: (props: OwnProps) => string | undefined
  ) =>
  <P extends {}>(Component: React.ComponentType<P & Partial<WithClassName>> & { ref: React.RefObject<TELement> }) =>
  (props: P & OwnProps) => {
    const ref = React.createRef<TELement>();

    const _action = () => {
      const element = ref.current;
      element && action(element, props);
    };

    React.useLayoutEffect(() => {
      _action();
    }, []);

    useUpdated(() => {
      _action();
    }, [props[optionsPropName]]);

    const cProps = omit(props, [optionsPropName, "ref", "isDisabled"]) as P;
    return (
      <Component
        ref={ref}
        {...cProps}
        className={classNames(props["className"], getClassName ? getClassName(props) : undefined)}
      />
    );
  };

export const makeWrap =
  <P extends {}>(
    withFn: <TElement extends HTMLElement>(
      Component: React.ComponentType<P>
    ) => React.FunctionComponent<P & React.HTMLProps<TElement> & { ref?: React.RefObject<TElement | null> }>
  ) =>
  <TElement extends HTMLElement>(tag: string) => {
    const Component = (props: P) => {
      const Tag = tag as unknown as React.ComponentType<React.HTMLProps<TElement>>;
      return <Tag {...props} />;
    };

    return withFn<TElement>(Component);
  };
